"use client";
import React, { memo } from "react";
import { OptimizedImage } from '@/components/ui/optimized-image';
import { PARTNERS_DATA, PARTNERS_SECTION, PARTNERS_STYLING, type Partner } from "@/utils/constants/partners";

const PartnerItem = memo(({ partner }: { partner: Partner }) => (
  <div className="partner-logo">
    {partner.logo ? (
      <div className="logo-image">
        <OptimizedImage 
          src={partner.logo}
          alt={partner.name}
          width={88}
          height={88}
          className={`w-full h-full object-contain ${partner.needsLargerImage ? 'enlarged-image' : ''}`}
          sizes="(max-width:768px) 80px, 88px"
        />
      </div>
    ) : (
      <div className="logo-circle">
        <span>{partner.name.charAt(0)}</span>
      </div>
    )}
    <p>{partner.name}</p>
  </div>
));

PartnerItem.displayName = 'PartnerItem';

export const PartnersSection = memo(() => {
  const partnersStyles = `
    .partners-container {
      overflow: hidden;
      width: 100%;
      white-space: nowrap;
    }
    
    .partners-track {
      display: inline-flex;
      animation: partners-scroll ${PARTNERS_SECTION.ANIMATION_DURATION} linear infinite;
      will-change: transform;
      gap: ${PARTNERS_SECTION.CONTAINER_GAP};
    }
    
    .partner-logo {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      min-width: ${PARTNERS_STYLING.PARTNER_MIN_WIDTH_DESKTOP};
      flex-shrink: 0;
    }
    
    .logo-circle {
      width: ${PARTNERS_STYLING.LOGO_SIZE_DESKTOP.WIDTH};
      height: ${PARTNERS_STYLING.LOGO_SIZE_DESKTOP.HEIGHT};
      background-color: #f3f4f6;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0.75rem;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    .logo-image {
      width: ${PARTNERS_STYLING.LOGO_SIZE_DESKTOP.WIDTH};
      height: ${PARTNERS_STYLING.LOGO_SIZE_DESKTOP.HEIGHT};
      background-color: #ffffff;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      border: 1px solid rgba(0, 0, 0, 0.05);
      overflow: hidden;
    }
    
    /* Immagini ingrandite per i loghi specifici che necessitano di maggiore visibilità */
    .enlarged-image {
      transform: scale(${PARTNERS_STYLING.ENLARGED_SCALE_DESKTOP});
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
    
    .logo-circle:hover,
    .logo-image:hover {
      transform: scale(${PARTNERS_STYLING.HOVER_SCALE});
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }
    
    .logo-circle span {
      font-size: 1.75rem;
      font-weight: 700;
      color: #4b5563;
    }
    
    .partner-logo p {
      font-size: 0.875rem;
      color: #374151;
      font-weight: 500;
      max-width: 7rem;
      line-height: 1.25;
      margin: 0;
      text-align: center;
    }
    
    @keyframes partners-scroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    
    @media (prefers-reduced-motion: reduce) {
      .partners-track {
        animation: none;
      }
    }
    
    @media (max-width: 768px) {
      .partner-logo {
        min-width: ${PARTNERS_STYLING.PARTNER_MIN_WIDTH_MOBILE};
      }
      
      .logo-circle, .logo-image {
        width: ${PARTNERS_STYLING.LOGO_SIZE_MOBILE.WIDTH};
        height: ${PARTNERS_STYLING.LOGO_SIZE_MOBILE.HEIGHT};
      }
      
      .enlarged-image {
        transform: scale(${PARTNERS_STYLING.ENLARGED_SCALE_MOBILE});
      }
      
      .logo-circle span {
        font-size: 1.5rem;
      }
      
      .partner-logo p {
        font-size: 0.8rem;
        max-width: 6rem;
      }
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: partnersStyles }} />

      <section className="py-16 bg-white">
  <div className="max-w-[1320px] mx-auto px-6 xl:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-gray-900 mb-2">
              {PARTNERS_SECTION.TITLE}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-2">
              {PARTNERS_SECTION.SUBTITLE}
            </p>
          </div>

          <div className="partners-container">
            <div className="partners-track" aria-label="Lista continua dei partner">
              {PARTNERS_DATA.map((partner) => (
                <PartnerItem key={partner.id} partner={partner} />
              ))}
              {/* Minimal duplicate to create seamless loop */}
              {PARTNERS_DATA.slice(0, 4).map((partner) => (
                <PartnerItem key={`dup-${partner.id}`} partner={partner} />
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              {PARTNERS_SECTION.FOOTER_TEXT}
            </p>
          </div>
        </div>
      </section>
    </>
  );
});

PartnersSection.displayName = 'PartnersSection';